// External Dependencies
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { NgbModule, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { JwtModule } from '@auth0/angular-jwt';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { MissingTranslationHandler, TranslateModule } from '@ngx-translate/core';
import { QRCodeModule } from 'angularx-qrcode';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { ToastrModule } from 'ngx-toastr';
import { InlineSVGModule } from 'ng-inline-svg-2';
// Routing & Configuration
import { AppRoutingModule } from './app-routing.module';
// Feature Modules
import { SharedModule } from './shared/shared.module';
import { ClubModule } from './modules/club/club.module';
import { UserModule } from './modules/user/user.module';
import { LoyaltyModule } from './modules/loyalty/loyalty.module';
import { VouchersModule } from './modules/vouchers/vouchers.module';
import { EcashModule } from './modules/ecash/ecash.module';
import { PurchaseModule } from './modules/purchase/purchase.module';
import { GuardianModule } from './modules/guardian/guardian.module';

// Utilities
import { MomentDateFormatter } from './shared/utilities/moment-date-formatter';
// @fgb/core
import {
  PopulationGuard,
  ProductCodeGuard,
  jwtTokenGetter,
  tmAuthConfig,
  tmOAuthConfig,
  FGBMissingTranslationHandler,
  provideAppConfig,
  provideTMAuth,
} from '@fgb/core';

// Component Library
import { CardTokenListModule } from '@fgb/portal-component-library/src/lib/ecash/card-token-list';
import { EcashDescriptionModule } from '@fgb/portal-component-library/src/lib/ecash/ecash-description';
import { LoadingModule } from '@fgb/portal-component-library/src/lib/shared/loading';
import { ErrorsModule } from '@fgb/portal-component-library/src/lib/shared/errors';
import { EarnPointsModule } from '@fgb/portal-component-library/src/lib/club/earn-points';
import { FaqSectionModule } from '@fgb/portal-component-library/src/lib/club/faq-section';
import { TermsAndConditionsModule } from '@fgb/portal-component-library/src/lib/club/terms-and-conditions';
import { CountryAndRegionsModule } from '@fgb/portal-component-library/src/lib/shared/country-and-regions';
import { DirectivesModule } from '@fgb/portal-component-library/src/lib/directives';
import { AuctionDetailsModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-details';
import { AuctionPanelModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-panel';
import { AuctionListModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-list';
import { LottosV2Module } from '@fgb/portal-component-library/src/lib/loyalty/lottos/lottos-v2';
import { MarketplaceModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace';
import {
  BadgeListItemModule,
  BadgeOverviewListModule,
  BadgeFeaturedListModule,
  BadgeAchievedModule,
} from '@fgb/portal-component-library/src/lib/loyalty/badges';
import { FeaturedRewardsModule } from '@fgb/portal-component-library/src/lib/loyalty/featured-rewards';
import { AddressBookModule } from '@fgb/portal-component-library/src/lib/user/address-book';
import { ShortLongDescriptionModule } from '@fgb/portal-component-library/src/lib/shared/short-long-description';
import { ContactUsModule } from '@fgb/portal-component-library/src/lib/club/contact-us';

// General Pages
import { AppComponent } from './app.component';
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { LoginAccountsPageComponent } from './pages/login-accounts-page/login-accounts-page.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';
import { TermsPageComponent } from './pages/terms-page/terms-page.component';
import { FaqPageComponent } from './pages/faq-page/faq-page.component';
import { ContactUsPageComponent } from './pages/contact-us-page/contact-us-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';

// Loyalty Pages
import { RewardsLayoutComponent } from './pages/rewards-layout/rewards-layout.component';
import { RewardsPageComponent } from './pages/rewards-page/rewards-page.component';
import { AuctionsPageComponent } from './pages/auctions-page/auctions-page.component';
import { LottosPageComponent } from './pages/lottos-page/lottos-page.component';
import { MarketplacePageComponent } from './pages/marketplace-page/marketplace-page.component';
import { EventsPageComponent } from './pages/events-page/events-page.component';
import { WalletPageComponent } from './pages/wallet-page/wallet-page.component';
import { AuctionDetailPageComponent } from './pages/auction-detail-page/auction-detail-page.component';
import { LottoDetailPageComponent } from './pages/lotto-detail-page/lotto-detail-page.component';
import { MarketplaceDetailPageComponent } from './pages/marketplace-detail-page/marketplace-detail-page.component';
import { LottosSuccessPageComponent } from './pages/lottos-page/lottos-success-page/lottos-success-page.component';
import { MarketplacePurchaseSuccessPageComponent } from './pages/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-page.component';
import { LottoWalletDetailsPageComponent } from './pages/lotto-wallet-details-page/lotto-wallet-details-page.component';
// Polls, Surveys & Trivia (Games)
import { GamesLayoutComponent } from './pages/games-layout/games-layout.component';
import { GamesPageComponent } from './pages/games-page/games-page.component';
import { QuizPageComponent, QuizPlayPageComponent } from './pages/quiz-page/quiz-page.component';
import { QuizLeaderboardPageComponent } from './pages/quiz-leaderboard-page/quiz-leaderboard-page.component';
import { PollsPageComponent } from './pages/polls-page/polls-page.component';
import { PredictorPageComponent, PredictorPlayComponent } from './pages/predictor-page/predictor-page.component';
import { PredictorLeaderboardPageComponent } from './pages/predictor-leaderboard-page/predictor-leaderboard-page.component';
// Account Pages
import { AccountDetailsPageComponent } from './pages/account-details-page/account-details-page.component';
import { ActivityPageComponent } from './pages/activity-page/activity-page.component';
// Ecash Pages
import { EcashPageComponent } from './pages/ecash-page/ecash-page.component';
import { EcashResultPageComponent } from './pages/ecash-page/ecash-result-page/ecash-result-page.component';
import { EcashSuccessPageComponent } from './pages/ecash-page/ecash-success-page/ecash-success-page.component';
import { EcashFailurePageComponent } from './pages/ecash-page/ecash-failure-page/ecash-failure-page.component';
import { EcashHistoryPageComponent } from './pages/ecash-history/ecash-history.component';
// Portal Products
import { PortalProductResultsPageComponent } from './pages/portal-products-pages/portal-product-results-page/portal-product-results-page.component';
import { PortalProductSuccessPageComponent } from './pages/portal-products-pages/portal-product-success-page/portal-product-success-page.component';
import { PortalProductFailurePageComponent } from './pages/portal-products-pages/portal-product-failure-page/portal-product-failure-page.component';
// Benefits
import { BenefitsPageComponent } from './pages/benefits-page/benefits-page.component';
// External Cash Transfer
import { CardPageComponent } from './pages/card-page/card-page.component';
// Club Pages
import { EarnPageComponent } from './pages/earn-page/earn-page.component';
import { GuardianPageComponent } from './pages/guardian-page/guardian-page.component';
import { LayoutModule } from './modules/layouts/layout.module';

import { LottosTermsPageComponent } from './pages/lottos-terms-page/lottos-terms-page.component';
import { MarketplaceTermsPageComponent } from './pages/marketplace-terms-page/marketplace-terms-page.component';
import { WorksheetPageComponent } from './pages/worksheet-page/worksheet-page.component';
import { SubscriptionsPageComponent } from './pages/subscriptions-page/subscriptions-page.component';
import { CartCheckoutPagePortalProductsComponent } from './pages/cart-checkout-page-portal-products/cart-checkout-page-portal-products.component';
import { CartCheckoutPageComponent } from './pages/cart-checkout-page/cart-checkout-page.component';
import { CartCheckoutResultsPageComponent } from './pages/cart-checkout-page/cart-checkout-results-page/cart-checkout-results-page.component';
import { CartCheckoutSuccessPageComponent } from './pages/cart-checkout-page/cart-checkout-success-page/cart-checkout-success-page.component';
import { ProductsPageComponent } from './pages/products-page/products-page.component';
import { PaymentPageComponent } from './pages/payment-page/payment-page.component';
import { CardLinkModule } from '@fgb/portal-component-library/src/lib/cardlinking';
import { SharedSearchBarModule } from '@fgb/portal-component-library/src/lib/shared/shared-search-bar';
import { AnnouncementsModule } from '@fgb/portal-component-library/src/lib/club/announcements';
import { CountdownModule } from '@fgb/portal-component-library/src/lib/shared/countdown';
import { OfferSearchBarModule } from '@fgb/portal-component-library/src/lib/club/offer-search-bar';
import { CartItemModule } from '@fgb/portal-component-library/src/lib/loyalty/cart-item';
import { ClubSelectorModule } from '@fgb/portal-component-library/src/lib/user/club-selector';
import { EditQuestionsModule } from '@fgb/portal-component-library/src/lib/user/edit-questions';
import { CampaignDetailsPageComponent } from './pages/campaign-details-page/campaign-details-page.component';
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';
import { WalletClaimedPageComponent } from './pages/wallet-claimed-page/wallet-claimed-page.component';
import { NotificationModule } from '@fgb/portal-component-library/src/lib/club/notification';
import { WelcomeComponent } from './modules/club/components/welcome/welcome.component';
import { WalletModule } from '@fgb/portal-component-library/src/lib/loyalty/wallet';
import { FeedModule } from '@fgb/portal-component-library/src/lib/feeds';
import { MarketplaceWalletPageComponent } from './pages/marketplace-wallet-page/marketplace-wallet-page.component';
import { AddPaymentCardDetailModule } from '@fgb/portal-component-library/src/lib/loyalty/add-payment-card-detail';
import { ScorecardModule } from '@fgb/portal-component-library/src/lib/loyalty/scorecards';
import { AuctionWalletDetailsPageComponent } from './pages/auction-wallet-details-page/auction-wallet-details-page.component';
import { RouterModule } from '@angular/router';
import { FilterModalModule } from './pages/rewards-page/filter-modal';
import { TicketTransferModule } from '@fgb/portal-component-library/src/lib/ticket-transfer';
import { MixedLoyaltyListModule } from '@fgb/portal-component-library/src/lib/loyalty/mixed-loyalty';
import { ChallengesPageComponent } from './pages/challenges-page/challenges-page.component';
import { GamesModule } from '@fgb/portal-component-library/src/lib/games';
import { TabComponent } from './modules/club/components/games-tab/tab.component';
import { CampaignsModule } from '@fgb/portal-component-library/src/lib/campaigns';
import { AccountLayoutPageComponent } from './pages/account-layout-page/account-layout-page.component';
import { buildPaymentTemplates } from '@fgb/portal-component-library/src/lib/shared/payment';
import { FeedPageComponent } from './pages/feed-page/feed-page.component';
import { BrandPageComponent } from './pages/brand-page/brand-page.component';
import { BrandListComponent } from './pages/brand-page/components/brand-list/brand-list.component';
import { BrandDetailPageComponent } from './pages/brand-page/brand-detail-page/brand-detail-page.component';
const tmSSOAuthConfig: tmOAuthConfig = {
  clientId: '0d1bc7bb2005.fortress.tbjrc-tbjrc.us',
  club: 'tbjrc',
  clientSecret: '1fa5a94b2eeddec0a6088a0068fdc308ea965f475f0a49bcc32c2ee06179ce19',
  tokenUri: 'https://auth.ticketmaster.com/api/oauth/token',
  loginUri: 'https://am.ticketmaster.com/tbjrc/sso',
  authoriseUri: 'https://auth.ticketmaster.com/as/authorization.oauth2',
};

const tmLoginAuthConfig: tmAuthConfig = {
  clientId: '0d1bc7bb2005.fortress.tbjrc-tbjrc.us',
  authUri: 'https://auth.ticketmaster.com/archtics/sign-in',
  loginUri: 'https://am.ticketmaster.com/tbjrc/sso',
  visualPresents: 'tbjrc',
};

@NgModule({
  declarations: [
    // General Pages
    AppComponent,
    MasterPageComponent,
    HomePageComponent,
    LoginPageComponent,
    LoginAccountsPageComponent,
    MaintenancePageComponent,
    TermsPageComponent,
    FaqPageComponent,
    ContactUsPageComponent,
    NotFoundPageComponent,
    FeedPageComponent,
    // Loyalty Pages

    RewardsLayoutComponent,
    RewardsPageComponent,
    AuctionsPageComponent,
    LottosPageComponent,
    MarketplacePageComponent,
    EventsPageComponent,
    WalletPageComponent,
    AuctionDetailPageComponent,
    LottoDetailPageComponent,
    MarketplaceDetailPageComponent,
    LottosSuccessPageComponent,
    MarketplacePurchaseSuccessPageComponent,
    LottoWalletDetailsPageComponent,
    AuctionWalletDetailsPageComponent,
    // Polls, Surveys & Trivia Pages (Games)
    GamesLayoutComponent,
    GamesPageComponent,
    QuizPageComponent,
    QuizPlayPageComponent,
    QuizLeaderboardPageComponent,
    PollsPageComponent,
    PredictorPageComponent,
    PredictorPlayComponent,
    PredictorLeaderboardPageComponent,

    // Account Pages
    AccountDetailsPageComponent,
    ActivityPageComponent,
    // Ecash Pages
    EcashPageComponent,
    EcashResultPageComponent,
    EcashSuccessPageComponent,
    EcashFailurePageComponent,
    EcashHistoryPageComponent,
    PaymentPageComponent,
    PortalProductResultsPageComponent,
    PortalProductSuccessPageComponent,
    PortalProductFailurePageComponent,
    // Guardians Pages
    GuardianPageComponent,
    // External Cash Transfers
    CardPageComponent,
    // Club Pages
    EarnPageComponent,
    BenefitsPageComponent,
    LottosTermsPageComponent,
    MarketplaceTermsPageComponent,
    WorksheetPageComponent,
    SubscriptionsPageComponent,
    CartCheckoutPagePortalProductsComponent,
    CartCheckoutPageComponent,
    CartCheckoutSuccessPageComponent,
    CartCheckoutResultsPageComponent,
    ProductsPageComponent,
    CampaignDetailsPageComponent,
    WelcomeComponent,
    // Registration
    RegistrationLayoutPageComponent,
    WalletClaimedPageComponent,
    MarketplaceWalletPageComponent,
    ChallengesPageComponent,
    AccountLayoutPageComponent,
    BrandPageComponent,
    BrandListComponent,
    BrandDetailPageComponent,
  ],
  imports: [
    // External Modules
    RouterModule,
    CarouselModule,
    AppRoutingModule,
    FormsModule,
    NgbModule,
    BrowserModule,
    HttpClientModule,
    ReactiveFormsModule,
    InlineSVGModule.forRoot(),
    QRCodeModule,
    BrowserAnimationsModule,
    EarnPointsModule,
    FaqSectionModule,
    TermsAndConditionsModule,
    AuctionDetailsModule,
    AuctionPanelModule,
    AuctionListModule,
    LottosV2Module,
    MarketplaceModule,

    ScorecardModule,
    BadgeListItemModule,
    BadgeOverviewListModule,
    BadgeFeaturedListModule,
    BadgeAchievedModule,
    ShortLongDescriptionModule,
    MixedLoyaltyListModule,
    LoadingModule,
    CountryAndRegionsModule,
    EcashDescriptionModule,
    CardTokenListModule,
    CountdownModule,
    ErrorsModule,
    EditQuestionsModule,
    FeaturedRewardsModule,
    AddressBookModule,
    WalletModule,
    NotificationModule,
    ContactUsModule,
    CampaignsModule,
    AddPaymentCardDetailModule,
    FilterModalModule,
    TicketTransferModule,
    FeedModule,
    ToastrModule.forRoot({
      maxOpened: 5,
    }),
    JwtModule.forRoot({
      config: {
        allowedDomains: ['http://localhost/portal2/api'],
        tokenGetter: jwtTokenGetter,
      },
    }),
    TranslateModule.forRoot({
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: FGBMissingTranslationHandler,
      },
    }),
    // Feature Modules
    LayoutModule,
    GuardianModule,
    PurchaseModule,
    ClubModule,
    EcashModule,
    VouchersModule,
    UserModule,
    SharedModule,
    LoyaltyModule,
    DirectivesModule,
    CardLinkModule,
    AnnouncementsModule,
    OfferSearchBarModule,
    SharedSearchBarModule,
    CartItemModule,
    ClubSelectorModule,
    GamesModule,
    TabComponent,
  ],
  providers: [
    provideAppConfig({
      dateOptions: {
        LongDateFormat: 'MMM D, yyyy h:mma',
      },
      paymentTemplates: buildPaymentTemplates(),
      componentVersions: {
        marketplace: 2,
        lottos: 2,
      },
    }),
    provideTMAuth({
      ssoAuth: tmSSOAuthConfig,
      loginAuth: tmLoginAuthConfig,
    }),
    PopulationGuard,
    ProductCodeGuard,
    { provide: NgbDateParserFormatter, useClass: MomentDateFormatter },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
